// #region Backend Call Types

interface BackendCallTypeDescriptor {
  /**
   * The numeric id value returned by the API for the call type. Unique within the backend.
   */
  id: number;
  /**
   * The developer-facing name of the call type. Should not be displayed to the end user, use {@link displayText} instead.
   */
  name: BackendCallTypeName;
  /**
   * The text value displayed in the UI for the call type.
   */
  displayText: string;
}

/**
 * The developer-facing names of the call types as they are tracked in the backend.
 * Should not be displayed to the end user, use {@link BackendCallType} instead.
 */
export enum BackendCallTypeName {
  Abandoned = 'Abandoned',
  Unbooked = 'Unbooked',
  Excused = 'Excused',
  Booked = 'Booked',
  NotLead = 'NotLead',
}

/**
 * The call types as they are tracked in the backend, mapped by name.
 */
const BackendCallTypesByName = {
  [BackendCallTypeName.Abandoned]: {
    id: 0 as const,
    name: BackendCallTypeName.Abandoned,
    displayText: 'Abandoned',
  } satisfies BackendCallTypeDescriptor,
  [BackendCallTypeName.Unbooked]: {
    id: 1 as const,
    name: BackendCallTypeName.Unbooked,
    displayText: 'Unbooked',
  } satisfies BackendCallTypeDescriptor,
  [BackendCallTypeName.Excused]: {
    id: 2 as const,
    name: BackendCallTypeName.Excused,
    displayText: 'Excused',
  } satisfies BackendCallTypeDescriptor,
  [BackendCallTypeName.Booked]: {
    id: 3 as const,
    name: BackendCallTypeName.Booked,
    displayText: 'Booked',
  } satisfies BackendCallTypeDescriptor,
  [BackendCallTypeName.NotLead]: {
    id: 4 as const,
    name: BackendCallTypeName.NotLead,
    displayText: 'Not a Lead',
  } satisfies BackendCallTypeDescriptor,
} as const;

/**
 * The call types as they are tracked in the backend.
 * Mapped by name and id.
 */
export const BackendCallType = {
  // ----------- by name -----------
  [BackendCallTypeName.Abandoned]:
    BackendCallTypesByName[BackendCallTypeName.Abandoned],
  [BackendCallTypeName.Unbooked]:
    BackendCallTypesByName[BackendCallTypeName.Unbooked],
  [BackendCallTypeName.Excused]:
    BackendCallTypesByName[BackendCallTypeName.Excused],
  [BackendCallTypeName.Booked]:
    BackendCallTypesByName[BackendCallTypeName.Booked],
  [BackendCallTypeName.NotLead]:
    BackendCallTypesByName[BackendCallTypeName.NotLead],

  // ----------- by id -----------
  [BackendCallTypesByName[BackendCallTypeName.Abandoned].id]:
    BackendCallTypesByName[BackendCallTypeName.Abandoned],
  [BackendCallTypesByName[BackendCallTypeName.Unbooked].id]:
    BackendCallTypesByName[BackendCallTypeName.Unbooked],
  [BackendCallTypesByName[BackendCallTypeName.Excused].id]:
    BackendCallTypesByName[BackendCallTypeName.Excused],
  [BackendCallTypesByName[BackendCallTypeName.Booked].id]:
    BackendCallTypesByName[BackendCallTypeName.Booked],
  [BackendCallTypesByName[BackendCallTypeName.NotLead].id]:
    BackendCallTypesByName[BackendCallTypeName.NotLead],
} as const;

export type BackendCallTypeValue =
  (typeof BackendCallType)[BackendCallTypeName]['id'];

export const AllBackendCallTypes = Object.values(BackendCallTypesByName);

/**
 * Checks if the call type is known. Returns true if the passed type is a valid BackendCallTypeName.
 * @param type - The call type to check.
 * @returns True if the call type is known, false otherwise.
 */
export const isKnownBackendCallType = (
  type: unknown,
): type is BackendCallTypeName => {
  return (
    typeof type === 'string' &&
    BackendCallTypesByName[type as BackendCallTypeName] !== undefined
  );
};

// #endregion Backend Call Types

// #region UI Call Types

interface UICallTypeDescriptor {
  /**
   * The unique id of the call type in the UI.
   */
  id: number;
  /**
   * The numeric id value returned by the API for the call type (not necessarily unique).
   */
  refId: number;
  /**
   * The developer-facing name of the call type. Should not be displayed to the end user, use {@link displayText} instead.
   */
  name: UICallTypeName;
  /**
   * The text value displayed in the UI for the call type.
   */
  displayText: string;
  /**
   * The description of the call type displayed in the UI.
   */
  description: string | null;
}

/**
 * The user-facing names of the call types as they are displayed in the UI.
 * Should not be used to track the call type in the backend, use {@link UICallType} instead.
 */
export enum UICallTypeName {
  Abandoned = 'Abandoned',
  Unbooked = 'Unbooked',
  ExcusedNotServiceRequest = 'ExcusedNotServiceRequest',
  ExcusedExistingJob = 'ExcusedExistingJob',
  Booked = 'Booked',
  NotLead = 'NotLead',
}

/**
 * The call types as they are displayed in the UI. Mapped by name.
 */
const UICallTypesByName = {
  [UICallTypeName.Abandoned]: {
    id: 0 as const,
    refId: BackendCallType.Abandoned.id,
    name: UICallTypeName.Abandoned,
    displayText: BackendCallType.Abandoned.displayText,
    description: null,
  } satisfies UICallTypeDescriptor,
  [UICallTypeName.Unbooked]: {
    id: 1 as const,
    refId: BackendCallType.Unbooked.id,
    name: UICallTypeName.Unbooked,
    displayText: BackendCallType.Unbooked.displayText,
    description: 'This was a service request that was not booked.',
  } satisfies UICallTypeDescriptor,
  [UICallTypeName.ExcusedNotServiceRequest]: {
    id: 102 as const,
    refId: BackendCallType.Excused.id,
    name: UICallTypeName.ExcusedNotServiceRequest,
    displayText: 'Excused - Not service request',
    description: 'This was not a service request. Excuse it.',
  } satisfies UICallTypeDescriptor,
  [UICallTypeName.ExcusedExistingJob]: {
    id: 202 as const,
    refId: BackendCallType.Excused.id,
    name: UICallTypeName.ExcusedExistingJob,
    displayText: 'Excused - Existing job',
    description: 'This call was related to an existing job.',
  } satisfies UICallTypeDescriptor,
  [UICallTypeName.Booked]: {
    id: 3 as const,
    refId: BackendCallType.Booked.id,
    name: UICallTypeName.Booked,
    displayText: 'Booked',
    description: 'A job was booked for this call.',
  } satisfies UICallTypeDescriptor,
  [UICallTypeName.NotLead]: {
    id: 4 as const,
    refId: BackendCallType.NotLead.id,
    name: UICallTypeName.NotLead,
    displayText: 'Not a Lead',
    description: null,
  } satisfies UICallTypeDescriptor,
} as const;

/**
 * The call types available in the UI, mapped by name and id.
 */
export const UICallType = {
  // ----------- by name -----------
  [UICallTypeName.Abandoned]: UICallTypesByName[UICallTypeName.Abandoned],
  [UICallTypeName.Unbooked]: UICallTypesByName[UICallTypeName.Unbooked],
  [UICallTypeName.ExcusedNotServiceRequest]:
    UICallTypesByName[UICallTypeName.ExcusedNotServiceRequest],
  [UICallTypeName.ExcusedExistingJob]:
    UICallTypesByName[UICallTypeName.ExcusedExistingJob],
  [UICallTypeName.Booked]: UICallTypesByName[UICallTypeName.Booked],
  [UICallTypeName.NotLead]: UICallTypesByName[UICallTypeName.NotLead],

  // ----------- by id -----------
  [UICallTypesByName[UICallTypeName.Abandoned].id]:
    UICallTypesByName[UICallTypeName.Abandoned],
  [UICallTypesByName[UICallTypeName.Unbooked].id]:
    UICallTypesByName[UICallTypeName.Unbooked],
  [UICallTypesByName[UICallTypeName.ExcusedNotServiceRequest].id]:
    UICallTypesByName[UICallTypeName.ExcusedNotServiceRequest],
  [UICallTypesByName[UICallTypeName.ExcusedExistingJob].id]:
    UICallTypesByName[UICallTypeName.ExcusedExistingJob],
  [UICallTypesByName[UICallTypeName.Booked].id]:
    UICallTypesByName[UICallTypeName.Booked],
  [UICallTypesByName[UICallTypeName.NotLead].id]:
    UICallTypesByName[UICallTypeName.NotLead],
} as const;

export const AllUICallTypes = Object.values(UICallTypesByName);

export type UICallTypeValue = (typeof UICallType)[UICallTypeName]['id'];

/**
 * Checks if the call type is known. Returns true if the passed type is a valid UICallTypeName.
 * @param type - The call type to check.
 * @returns True if the call type is known, false otherwise.
 */
export const isKnownUICallType = (type: unknown): type is UICallTypeName => {
  return (
    typeof type === 'string' &&
    UICallTypesByName[type as UICallTypeName] !== undefined
  );
};

// #endregion UI Call Types
